import {
    ErrorResponse,
    InMemoryWebStorage,
    User,
    UserManager,
    type UserManagerSettings,
    type UserProfile,
    WebStorageStateStore,
} from 'oidc-client-ts';
import type { OAuthConfig, RioProfile, SessionRenewedResult } from './types';

const RETRY_SIGNIN_TIMEOUT_IN_MS = 30000;

const stripSnakeProps = (rawProfile: UserProfile) => {
    const { family_name, given_name, client_id, ...strippedProfile } = rawProfile;
    return strippedProfile;
};

const mapUserProfile = (profile: UserProfile): RioProfile => ({
    // sub: profile.sub,
    azp: profile.azp,
    givenName: profile.given_name,
    familyName: profile.family_name,
    name: profile.name,
    locale: profile.locale,
    email: profile.email,
    ...stripSnakeProps(profile),
});

const adaptPublishedInfo = (result: User): SessionRenewedResult => ({
    accessToken: result.access_token,
    idToken: result.profile,
    locale: result.profile?.locale ?? 'en-GB',
    profile: mapUserProfile(result.profile),
});

const isErrorResponse = (error: unknown): error is ErrorResponse => error instanceof ErrorResponse;

const retrySigninSilent = (
    oauthConfig: OAuthConfig,
    userManager: UserManager,
    options?: { retrySigninTimeoutMs?: number }
) => {
    userManager.signinSilent().catch((error: unknown) => {
        if (isErrorResponse(error) && error.error === 'login_required') {
            oauthConfig.onSessionExpired();
        } else {
            setTimeout(
                () => retrySigninSilent(oauthConfig, userManager),
                options?.retrySigninTimeoutMs || RETRY_SIGNIN_TIMEOUT_IN_MS
            );
        }
    });
};

type LoginConfig = {
    redirectUri: string;
    silentRedirectUri?: string;
    authority: string;
    clientId: string;
    oauthScope?: string[];
};

export const createUserManager = (loginConfig: LoginConfig) => {
    const redirectUri = loginConfig.redirectUri;
    const silentRedirectUri = loginConfig.silentRedirectUri;

    const settings: UserManagerSettings = {
        authority: `${loginConfig.authority}`,
        client_id: `${loginConfig.clientId}`,
        loadUserInfo: false,
        redirect_uri: `${redirectUri}`,
        response_type: 'code',
        scope: loginConfig.oauthScope?.join(' '),
        silent_redirect_uri: `${silentRedirectUri || redirectUri}`,
        includeIdTokenInSilentRenew: false,
        automaticSilentRenew: true,
        monitorSession: true,
        staleStateAgeInSeconds: 600,
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
        filterProtocolClaims: false,
    };

    return new UserManager(settings);
};

export const configureUserManager = (oauthConfig: OAuthConfig, userManager: UserManager) => {
    userManager.events.addUserLoaded(user => {
        oauthConfig.onSessionRenewed(adaptPublishedInfo(user));
    });

    userManager.events.addUserUnloaded(() => {
        oauthConfig.onSessionExpired();
    });

    userManager.events.addSilentRenewError(() => {
        retrySigninSilent(oauthConfig, userManager);
    });

    userManager.events.addUserSignedOut(() => {
        oauthConfig.onSessionExpired();
    });

    return userManager;
};

export const configureMockUserManager = (
    oauthConfig: OAuthConfig,
    loginConfig?: { mockLocale: string; mockTenant: string }
): UserManager => {
    const signinSilent = () => {
        const userSettings = {
            access_token: 'valid-mocked-oauth-bogus-token',
            profile: {
                iss: 'Issuer Identifier',
                aud: 'Audience(s): client_id',
                exp: 10,
                iat: 5,
                account: 'mock-account-id',
                azp: 'test-client',
                email: 'test@example.com',
                family_name: 'Mustermann',
                given_name: 'Max',
                name: 'Max Mustermann',
                sub: 'prod-rio-users:mock-user-profile-id',
                locale: loginConfig?.mockLocale ?? 'en-GB',
                tenant: loginConfig?.mockTenant ?? 'rio-eu.test',
            },
            id_token: 'id_token',
            session_state: 'session_state',
            token_type: 'token_type',
            scope: 'scope',
            expires_at: 100000,
            state: 'state',
        };

        const user = new User(userSettings);
        oauthConfig.onSessionRenewed(adaptPublishedInfo(user));
        return Promise.resolve(user);
    };

    const clearStaleState = () => {
        console.info('[configuration/login/oidc-session] Stale state cleared');
        return Promise.resolve();
    };

    return { signinSilent, clearStaleState } as UserManager;
};
